import React, { createContext, useState, useContext } from "react";
import { loginService } from "../services/authService";
// 🔥 Importamos el nuevo servicio
import { getProfileService } from "../services/profileService";
import { useActiveRole } from "./ActiveRoleContext";
import { filterRolesForMaps } from "../constants/userRoles";

const AuthContext = createContext();

/** Error de "te autenticaste bien, pero ningun rol tuyo entra a maps". El Login lo distingue. */
export class NoMapsAccessError extends Error {
  constructor() {
    super("Tu usuario no tiene un rol con acceso a maps.");
    this.name = "NoMapsAccessError";
  }
}

// OJO: va DENTRO de ActiveRoleProvider (ver App.jsx) porque usa useActiveRole.
export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")) || null);
  const [loading, setLoading] = useState(false);
  const { setAvailableRoles, setActiveRole, clearActiveRole } = useActiveRole();

  const login = async (email, password) => {
    setLoading(true);
    try {
      // 1. Hacemos el Login para obtener el Token y los roles base
      const authData = await loginService(email, password);
      const accessToken = authData.accessToken;

      // 2. Quien entra a maps se decide ACA, antes de persistir nada: el backend autentica a
      //    cualquier rol (el login es el mismo de la app), asi que la lista blanca es del front.
      //    `authData.user.roles` viene como array de strings (el enum Role del backend).
      const allowedRoles = filterRolesForMaps(authData.user?.roles);
      if (allowedRoles.length === 0) {
        throw new NoMapsAccessError();
      }

      // 3. Usamos el Token para obtener el Perfil Completo (Nombre, Apellido, etc.)
      const profileData = await getProfileService(accessToken);

      // 4. Combinamos los datos (Roles del login + Datos del perfil)
      const fullUserData = {
        ...authData.user,   // Trae roleId, roles, locked, enabled
        ...profileData      // Trae name, lastName, phone, idCard, gender, etc.
      };

      // 5. Guardamos todo en los estados
      setToken(accessToken);
      setUser(fullUserData);

      // 6. Guardamos en localStorage para persistir la sesión
      localStorage.setItem("token", accessToken);
      localStorage.setItem("user", JSON.stringify(fullUserData));

      // 7. Roles elegibles de la sesion. Con uno solo se elige aqui mismo y no se le pregunta
      //    nada al usuario; con varios, el Login manda al selector.
      setAvailableRoles(allowedRoles);
      if (allowedRoles.length === 1) {
        setActiveRole(allowedRoles[0]);
      }

      return { ...fullUserData, allowedRoles };
    } catch (error) {
      console.error("❌ Falló el Login:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Sin esto el rol elegido sobreviviria al logout y el proximo usuario entraria con el rol
    // del anterior.
    clearActiveRole();
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
